// src/Components/Home.jsx
import React from 'react';
import { useEffect, useState, useRef } from 'react';
import '../CSS/Home.css';
import InfoBox from './InfoBox';
import TabbedInfoCard from './TabbedInfoCard';
import Footer from './Footer';
import Particles from './Particles';
import emailjs from '@emailjs/browser';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { ref, get } from 'firebase/database';
import { db } from '../firebaseConfig';

import { Cloudinary } from '@cloudinary/url-gen';
import {AdvancedImage} from '@cloudinary/react';
import {fill} from "@cloudinary/url-gen/actions/resize";
import theaterJS from 'theaterjs';

function Home() {
  // Create refs for scroll targets
  const aboutRef = useRef(null);
  const projectsRef = useRef(null);
  const contactRef = useRef(null);

  // Add state for image flip and theme
  const [isImageFlipped, setIsImageFlipped] = useState(false);
  const [isHackerTheme, setIsHackerTheme] = useState(false);

  // State for projects from Firebase
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  // Contact form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const formRef = useRef();
  const captchaRef = useRef();

  // Create a Cloudinary instance and set your cloud name.
  const cld = new Cloudinary({
    cloud: {
      cloudName: 'dksnd2kit'
    }
  });

    // Instantiate a CloudinaryImage object for the image with the public ID, 'docs/models'.
  const myTranscript = cld.image('Screenshot_2025-07-24_095451_sgibq1');
  const facbooklogo = cld.image('facebook_gray_zhwic6'); 
  const linkedinlogo = cld.image('linkedin_gray_vpqqsa');
  const whatsapp_logo = cld.image('whatsapp_gray_hq2esq');
  const x_logo = cld.image('x_gray_h45exo');
  const instagram_logo = cld.image('inster_gray_pqjzmg');
  const github_logo = cld.image('github_gray_wnezil');
  const hackthebox_logo = cld.image('hackthebox_gray_yuiekh');
  const discord_gray_tleida = cld.image('discord_gray_ofgcvl');
  const ud_green = cld.image('udgreen_evit8o');
  const ud_blue = cld.image('udblue_cmrxyg');

  const phone_logo = cld.image('Untitled_design-removebg-preview_ziceoj');
  const email_logo = cld.image('Untitled_design__1_-removebg-preview_u6snhp');
  


  const me = cld.image('me4_xw0nfg');
  const hacker_avatar = cld.image('hacker_avataor_zpfkjx');
  const githublogo = cld.image('github_logo'); // Add your GitHub logo
  const livelogo = cld.image('live_logo'); // Add your live demo logo 
  const hackeraboutme = cld.image('hackeraboutme_vck4je');
  const hackeraboutme2 = cld.image('hackeraboutme2_k7tlxa');



  // Resize to maintain aspect ratio (720x311 = 2.3:1)
  facbooklogo.resize(fill().width(40).height(40));
  linkedinlogo.resize(fill().width(40).height(40));
  whatsapp_logo.resize(fill().width(40).height(40));
  x_logo.resize(fill().width(40).height(40)); 
  githublogo.resize(fill().width(25).height(25));
  livelogo.resize(fill().width(25).height(25));
  instagram_logo.resize(fill().width(40).height(40));
  github_logo.resize(fill().width(40).height(40));
  hackthebox_logo.resize(fill().width(40).height(40));
  discord_gray_tleida.resize(fill().width(40).height(40));


  phone_logo.resize(fill().width(40).height(40));
  email_logo.resize(fill().width(40).height(40));

  ud_green.resize(fill().width(115).height(50));
  ud_blue.resize(fill().width(115).height(50));

  // Update favicon based on theme
  useEffect(() => {
    const favicon = document.querySelector("link[rel*='icon']") || document.createElement('link');
    favicon.type = 'image/png';
    favicon.rel = 'icon';
    
    // Use the appropriate Cloudinary image URL based on theme
    if (isHackerTheme) {
      favicon.href = ud_blue.toURL();
    } else {
      favicon.href = ud_green.toURL();
    }
    
    document.head.appendChild(favicon);
  }, [isHackerTheme]);

  // Fetch projects from Firebase
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projectsRef = ref(db, 'projects');
        const snapshot = await get(projectsRef);
        if (snapshot.exists()) {
          const projectsData = [];
          snapshot.forEach((childSnapshot) => {
            projectsData.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          setProjects(projectsData);
        } else {
          setProjects([]);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        setProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    };
    
    fetchProjects();
  }, []);

  useEffect(() => {
    const theater = theaterJS();

    theater
      .on('type:start, erase:start', function () {
        theater.getCurrentActor().$element.classList.add('actor__content--typing');
      })
      .on('type:end, erase:end', function () {
        theater.getCurrentActor().$element.classList.remove('actor__content--typing');
      });

    theater
      .addActor('mainTitle', { speed: 0.8, accuracy: 0.9, element: document.getElementById('mainTitle') })
      .addScene('mainTitle:HI I\'M UDANA', 1000)
  }, []);

  // Navigation scroll function
  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  //projects functions with categories
  const [selectedProjectCategory, setSelectedProjectCategory] = useState('All');
  const [showAllProjects, setShowAllProjects] = useState(false);
  
  const allProjectCategories = ['All', 'Web', 'Mobile', 'UXUI', 'Networking', 'Cybersecurity', 'Figma'];
  
  // Filter categories in hacker theme to show only All, Cybersecurity, and Networking
  const projectCategories = isHackerTheme 
    ? ['All', 'Cybersecurity', 'Networking']
    : allProjectCategories;

  // Filter projects based on category and hacker theme
  let filteredProjects = selectedProjectCategory === 'All' 
    ? projects 
    : projects.filter(proj => proj.category === selectedProjectCategory);

  // In hacker theme, only show Networking and Cybersecurity projects
  if (isHackerTheme) {
    filteredProjects = filteredProjects.filter(proj => 
      proj.category === 'Networking' || proj.category === 'Cybersecurity'
    );
  }

  // Show only first 3 projects or all based on showAllProjects state
  const displayedProjects = showAllProjects ? filteredProjects : filteredProjects.slice(0, 3);

  // Reset showAllProjects when category changes
  const handleCategoryChange = (category) => {
    setSelectedProjectCategory(category);
    setShowAllProjects(false);
  };

  //certificate section
  const [selectedCategory, setSelectedCategory] = useState('All');

  const certificates = [
    
  ];

  const categories = ['All', 'ML', 'Cybersecurity', 'UXUI', 'Frontend', 'Mobile'];

  const filteredCertificates =
    selectedCategory === 'All'
      ? certificates
      : certificates.filter(cert => cert.category === selectedCategory);

  // Updated click handler for image flip with theme change
  const handleImageClick = () => {
    setIsImageFlipped(!isImageFlipped);
    setIsHackerTheme(!isHackerTheme);
  };

  // Computer-related quotes
  const quotes = [
    { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
    { text: "Code is like humor. When you have to explain it, it's bad.", author: "Cory House" },
    { text: "First, solve the problem. Then, write the code.", author: "John Johnson" },
    { text: "Any fool can write code that a computer can understand. Good programmers write code that humans can understand.", author: "Martin Fowler" },
    { text: "Talk is cheap. Show me the code.", author: "Linus Torvalds" },
    { text: "The best way to predict the future is to invent it.", author: "Alan Kay" },
    { text: "Security is not a product, but a process.", author: "Bruce Schneier" },
    { text: "The Internet is the first thing that humanity has built that humanity doesn't understand.", author: "Eric Schmidt" },
    { text: "Passwords are like underwear: don't let people see it, change it very often, and you shouldn't share it with strangers.", author: "Chris Pirillo" },
    { text: "In cybersecurity, the only constant is change.", author: "Unknown" },
    { text: "The computer was born to solve problems that did not exist before.", author: "Bill Gates" },
    { text: "It's not a bug. It's an undocumented feature.", author: "Anonymous" },
    { text: "Hackers are breaking the systems for profit. Before, it was about intellectual curiosity and pursuit of knowledge.", author: "Kevin Mitnick" },
    { text: "The best defense is a good offense.", author: "Cybersecurity Principle" },
    { text: "Simplicity is the soul of efficiency.", author: "Austin Freeman" }
  ];

  // Auto-rotating quotes every 10 seconds
  const [currentQuote, setCurrentQuote] = useState(() => quotes[Math.floor(Math.random() * quotes.length)]);

  useEffect(() => {
    const quoteInterval = setInterval(() => {
      setCurrentQuote(quotes[Math.floor(Math.random() * quotes.length)]);
    }, 10000); // Change quote every 10 seconds

    return () => clearInterval(quoteInterval);
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle captcha verification
  const onCaptchaVerify = (token) => {
    setCaptchaToken(token);
  };

  const onCaptchaExpire = () => {
    setCaptchaToken(null);
  };

  // Handle form submission with EmailJS
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check if captcha is verified
    if (!captchaToken) {
      setSubmitStatus('captcha-error');
      setTimeout(() => setSubmitStatus(''), 3000);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('');

    // EmailJS service configuration
    const serviceID = 'service_9o8ygw9';
    const templateID = 'template_j6pgwwt';
    const publicKey = 'ZWUF48pqS5d8NNq5U';

    emailjs.sendForm(serviceID, templateID, formRef.current, publicKey)
      .then((result) => {
        console.log('Email sent successfully:', result.text);
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setCaptchaToken(null);
        if (captchaRef.current) {
          captchaRef.current.resetCaptcha();
        }
        setTimeout(() => setSubmitStatus(''), 5000);
      }, (error) => {
        console.error('Email send failed:', error);
        console.error('Error text:', error.text);
        console.error('Error status:', error.status);
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus(''), 5000);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className={`homepageContainer ${isHackerTheme ? 'hacker-theme' : ''}`}>
      {/* Add Particles as background */}
      <div className="particles-background">
        <Particles
          particleColors={['#ffffff', '#ffffff']}
          particleCount={500}
          particleSpread={10}
          speed={0.5}
          particleBaseSize={50}
          moveParticlesOnHover={true}
          alphaParticles={true}
          disableRotation={true}
        />
      </div>

      {/* Navigation Bar */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <AdvancedImage 
              cldImg={isHackerTheme ? ud_blue : ud_green} 
              alt="UD Logo" 
              className="logo"
            />
          </div>
          <div className="nav-links">
            <button className="nav-btn" onClick={() => scrollToSection(aboutRef)}>
              About Me
            </button>
            <button className="nav-btn" onClick={() => scrollToSection(projectsRef)}>
              Projects
            </button>
            <button className="nav-btn" onClick={() => scrollToSection(contactRef)}>
              Contact Me
            </button>
          </div>
        </div>
      </nav>


      {/* Header Section */}
      <div className="headerSection">
        <div className="headerTitles">
          <h1 id="mainTitle" className="actor__content homepageTitle"></h1>
            <h2 className="homepageSubTitle">Cybersecurity Enthusiast | Full-stack Developer</h2>
        </div>
        <div className="headerImageContainer">
          <div 
            className={`image-flip-container ${isImageFlipped ? 'flipped' : ''}`}
            onClick={handleImageClick}
          >
            <div className="image-flip-inner">
              <div className="image-flip-front">
                <AdvancedImage cldImg={me} alt="My Profile" className="homepageImage"/>
              </div>
              <div className="image-flip-back">
                <AdvancedImage cldImg={hacker_avatar} alt="Hacker Avatar" className="homepageImage"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quote Box Section */}
      <div className="quote-section">
        <div className={`quote-box ${isHackerTheme ? 'hacker-theme' : ''}`}>
          <p className="quote-text"><strong>"{currentQuote.text.toUpperCase()}"</strong></p>
          <p className="quote-author">- {currentQuote.author.toUpperCase()}</p>
          <div className="easter-egg-message">
            {isHackerTheme ? (
              <p>🌟 Psst... Click my avatar to return to the light side 🌟</p>
            ) : (
              <p> Click my image above to enter the hacker realm... </p>
            )}
          </div>
        </div>
      </div>

      
      

      {/* About Me Section */}
      <div ref={aboutRef} className="section-marker">
      <h2 className="sectionTitle">#ABOUT ME</h2>
        <div className="profileSection">
          <div>
            <AdvancedImage 
              cldImg={isHackerTheme ? hackeraboutme2 : hackeraboutme} 
              alt="About Me Image" 
              className="aboutMeImage"
            />
          </div>
          <div className="info-card-container">
            <InfoBox isHackerTheme={isHackerTheme} />
          </div>
        </div>
        <div className="tabbed-info-card-container">
          <TabbedInfoCard isHackerTheme={isHackerTheme} />  
        </div>  
      </div>

      {/* Projects Section */}
      <div ref={projectsRef} className="section-marker">
        <div className="projectsPage">
          <h1 className="projects-title">#My-Projects</h1>
          
          <div className="projectCategoryButtons">
            {projectCategories.map((cat, index) => (
              <button
                key={index}
                className={`projectCategoryBtn ${selectedProjectCategory === cat ? 'active' : ''}`}
                onClick={() => handleCategoryChange(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
          
          {projectsLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', fontSize: '1.2rem', color: '#666' }}>
              Loading projects...
            </div>
          ) : (
            <>
              <div className="projectsGrid">
                {displayedProjects.map((proj, i) => (
                  <div className="projectBox" key={proj.id || i}>
                    <div className="project-image-container">
                      <img src={proj.imageUrl} alt={proj.title} className="projectImage" />
                    </div>
                    <h3 className="projectTitle">{proj.title}</h3>
                    <p className="projectDescription">{proj.description}</p>
                    <div className="project-links">
                      <a href={proj.githubUrl} target="_blank" rel="noopener noreferrer" className="project-link">
                        <AdvancedImage cldImg={github_logo} alt="GitHub" className="project-icon"/>
                      </a>
                      <a href={proj.liveUrl} target="_blank" rel="noopener noreferrer" className="project-link">
                        <AdvancedImage cldImg={linkedinlogo} alt="Live Demo" className="project-icon"/>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              
              {filteredProjects.length > 3 && (
                <div className="show-more-container">
                  <button 
                    className="show-more-btn"
                    onClick={() => setShowAllProjects(!showAllProjects)}
                  >
                    {showAllProjects ? 'Show Less' : 'Wanna See More?'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Contact Section */}
      <div ref={contactRef} className="section-marker">
        <div className="contactSection">
          <h2 className="sectionTitle">#Contact-Me</h2>
          <div className="contact-layout">
            <div className="contact-info">
              <div className="contact-message">
                <div className="message-title">
                  <h3>Let's Connect</h3>
                </div>
                <p>Have a project in mind or need help with development or security? Drop me a message, I'm open to collaborations and always ready to brainstorm.</p>
              </div>
              <p><AdvancedImage cldImg={phone_logo} alt="phone_logo" className="icon"/> +94-74-229-2189</p>
              <p><AdvancedImage cldImg={email_logo} alt="email_logo" className="icon"/> udanasenanayake.live@gmail.com</p>
              <div className="socialIcons">
                <a href="https://linkedin.com" target="_blank" className="icon"><AdvancedImage cldImg={linkedinlogo} alt="linkedinlogo" className="icon"/></a>
                <a href="https://facebook.com" target="_blank" className="icon"><AdvancedImage cldImg={facbooklogo} alt="facbooklogo" className="icon"/></a>
                <a href="https://wa.me/1234567890" target="_blank" className="icon"><AdvancedImage cldImg={whatsapp_logo} alt="whatsapp_logo" className="icon"/></a>
                <a href="https://twitter.com" target="_blank" className="icon"><AdvancedImage cldImg={x_logo} alt="x_logo" className="icon"/></a>
                <a href="https://instagram.com" target="_blank" className="icon"><AdvancedImage cldImg={instagram_logo} alt="instagram_logo" className="icon"/></a>
                <a href="https://github.com" target="_blank" className="icon"><AdvancedImage cldImg={github_logo} alt="github_logo" className="icon"/></a>
                <a href="https://discord.com" target="_blank" className="icon"><AdvancedImage cldImg={discord_gray_tleida} alt="discord_logo" className="icon"/></a>
                <a href="https://hackthebox.com" target="_blank" className="icon"><AdvancedImage cldImg={hackthebox_logo} alt="hackthebox_logo" className="icon"/></a>
            </div>
            </div>
            
            <div className="contact-form-card">
              <h3 className="form-title">Quick Contact</h3>
              <form ref={formRef} className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <textarea
                    id="message"
                    name="message"
                    rows="3"
                    placeholder="Your message here..."
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>
                
                <div className="form-group captcha-container">
                  <HCaptcha
                    ref={captchaRef}
                    sitekey="5cdc3a66-2846-496e-9e70-33e63e9aac0e"
                    onVerify={onCaptchaVerify}
                    onExpire={onCaptchaExpire}
                    theme={isHackerTheme ? "dark" : "light"}
                  />
                </div>
                
                {submitStatus === 'success' && (
                  <div className="submit-message success-message">
                    ✓ Message sent successfully! I'll get back to you soon.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="submit-message error-message">
                    ✗ Failed to send message. Please try again or email me directly.
                  </div>
                )}
                
                {submitStatus === 'captcha-error' && (
                  <div className="submit-message error-message">
                    ⚠ Please complete the captcha verification first.
                  </div>
                )}
                
                <button type="submit" className="send-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
        <div className="footer-container">
          <Footer />
        </div>
      </div>
    </div>
  );
}

export default Home;
